/**
 * Service Worker Registration and Management
 * Handles service worker lifecycle and offline functionality
 */

import { config } from '../config/environment';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

/**
 * Register service worker
 */
export function registerSW() {
  if (!config.features.enableOfflineMode) {
    console.log('Service Worker: Offline mode disabled');
    return;
  }

  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);

    if (publicUrl.origin !== window.location.origin) {
      console.log('Service Worker: Different origin, skipping registration');
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/sw.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl);

        navigator.serviceWorker.ready.then(() => {
          console.log('Service Worker: Ready in localhost mode');
        });
      } else {
        registerValidSW(swUrl);
      }
    });
  } else {
    console.log('Service Worker: Not supported in this browser');
  }
}

/**
 * Register valid service worker
 */
function registerValidSW(swUrl) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      console.log('Service Worker: Registered successfully');

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;

        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log(
                'Service Worker: New content available, please refresh'
              );

              // Notify user about update
              showUpdateNotification();
            } else {
              console.log('Service Worker: Content cached for offline use');

              // Notify user about offline capability
              showOfflineNotification();
            }
          }
        };
      };
    })
    .catch(error => {
      console.error('Service Worker: Registration failed', error);
    });
}

/**
 * Check if service worker is valid
 */
function checkValidServiceWorker(swUrl) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then(response => {
      const contentType = response.headers.get('content-type');

      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl);
      }
    })
    .catch(() => {
      console.log(
        'Service Worker: No internet connection, running in offline mode'
      );
    });
}

/**
 * Unregister service worker
 */
export function unregisterSW() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
        console.log('Service Worker: Unregistered');
      })
      .catch(error => {
        console.error('Service Worker: Unregistration failed', error);
      });
  }
}

/**
 * Update service worker
 */
export function updateSW() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.update();
        console.log('Service Worker: Update requested');
      })
      .catch(error => {
        console.error('Service Worker: Update failed', error);
      });
  }
}

/**
 * Skip waiting for new service worker
 */
export function skipWaiting() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
  }
}

/**
 * Get service worker version
 */
export function getSWVersion() {
  return new Promise(resolve => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const messageChannel = new MessageChannel();

      messageChannel.port1.onmessage = event => {
        resolve(event.data.version);
      };

      navigator.serviceWorker.controller.postMessage({ type: 'GET_VERSION' }, [
        messageChannel.port2,
      ]);
    } else {
      resolve(null);
    }
  });
}

/**
 * Cache specific URLs
 */
export function cacheUrls(urls) {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_URLS',
      urls,
    });
  }
}

/**
 * Check if app is running offline
 */
export function isOffline() {
  return !navigator.onLine;
}

/**
 * Add offline event listeners
 */
export function addOfflineListeners() {
  window.addEventListener('online', () => {
    console.log('App: Back online');
    showOnlineNotification();
  });

  window.addEventListener('offline', () => {
    console.log('App: Gone offline');
    showOfflineNotification();
  });
}

/**
 * Show update notification
 */
function showUpdateNotification() {
  if (config.environment.isDevelopment) {
    console.log('Service Worker: Update available');
    return;
  }

  // Create update notification
  const notification = document.createElement('div');
  notification.className = 'sw-update-notification';
  notification.innerHTML = `
    <div class="sw-notification-content">
      <p>A new version is available!</p>
      <button onclick="window.location.reload()">Refresh</button>
      <button onclick="this.parentElement.parentElement.remove()">Later</button>
    </div>
  `;

  // Add styles
  const style = document.createElement('style');
  style.textContent = `
    .sw-update-notification {
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4f46e5;
      color: white;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      max-width: 300px;
    }
    .sw-notification-content button {
      background: white;
      color: #4f46e5;
      border: none;
      padding: 8px 16px;
      margin: 8px 4px 0 0;
      border-radius: 4px;
      cursor: pointer;
    }
  `;

  document.head.appendChild(style);
  document.body.appendChild(notification);

  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (notification.parentElement) {
      notification.remove();
    }
  }, 10000);
}

/**
 * Show offline notification
 */
function showOfflineNotification() {
  // Disable offline notification banner completely
  console.log('App: Offline mode active - notification disabled');
  return;
}

/**
 * Show online notification
 */
function showOnlineNotification() {
  // Remove offline indicator if it exists
  const indicator = document.getElementById('offline-indicator');
  if (indicator) {
    indicator.remove();
  }

  // Disable online notification banner completely
  console.log('App: Back online - notification disabled');
  return;
}

/**
 * Remove any existing offline indicators
 */
export function removeOfflineIndicators() {
  const indicator = document.getElementById('offline-indicator');
  if (indicator) {
    indicator.remove();
  }
  
  const style = document.getElementById('offline-indicator-style');
  if (style) {
    style.remove();
  }
}

/**
 * Initialize service worker and offline functionality
 */
export function initializeServiceWorker() {
  // Remove any existing offline indicators first
  removeOfflineIndicators();

  // Register service worker
  registerSW();

  // Add offline listeners
  addOfflineListeners();

  // Don't show offline notifications
  console.log('App: Service worker initialized - offline notifications disabled');
}

export default {
  registerSW,
  unregisterSW,
  updateSW,
  skipWaiting,
  getSWVersion,
  cacheUrls,
  isOffline,
  addOfflineListeners,
  removeOfflineIndicators,
  initializeServiceWorker,
};
